import React, { useState, useEffect } from 'react';
import AdminHeader from './AdminHeader';
import ReportFilters from './ReportFilters';
import SalesChart from './SalesChart';
import ReportTable from './ReportTable';
import ReportSummary from './ReportSummary';
import '../../styles/admin/ReportsPage.css';

const API_URL = 'https://cake-shop-mern.onrender.com/api';

const ReportsPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState('sales');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalRevenue: 0,
    totalInvoices: 0,
    averageOrderValue: 0,
    topProducts: []
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, dateRange]);

  useEffect(() => {
    generateReportData();
  }, [filteredInvoices, products]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/invoice`),
        fetch(`${API_URL}/product`)
      ]);

      if (invoicesRes.ok) {
        const invoicesData = await invoicesRes.json();
        setInvoices(invoicesData);
      }

      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    end.setHours(23, 59, 59, 999);

    const filtered = invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return invoiceDate >= start && invoiceDate <= end;
    });

    setFilteredInvoices(filtered);
  };

  const generateReportData = () => {
    const totalRevenue = filteredInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const totalInvoices = filteredInvoices.length;
    const averageOrderValue = totalInvoices > 0 ? totalRevenue / totalInvoices : 0;

    const productSales = {};
    filteredInvoices.forEach(invoice => {
      invoice.items.forEach(item => {
        const key = item.productName || item.productId;
        if (!productSales[key]) {
          productSales[key] = {
            name: item.productName,
            quantity: 0,
            revenue: 0
          };
        }
        productSales[key].quantity += item.qty;
        productSales[key].revenue += item.lineTotal || (item.qty * item.unitPrice);
      });
    });

    const topProducts = Object.values(productSales)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    setReportData({
      totalRevenue,
      totalInvoices,
      averageOrderValue,
      topProducts
    });
  };

  const handleExport = () => {
    let csvContent = '';
    
    if (reportType === 'sales') {
      csvContent = 'Invoice Date,Invoice ID,Items Count,Total Amount\n';
      filteredInvoices.forEach(inv => {
        const date = new Date(inv.date).toLocaleDateString();
        const itemsCount = inv.items?.length || 0;
        csvContent += `${date},${inv._id},${itemsCount},${inv.totalAmount.toFixed(2)}\n`;
      });
    } else if (reportType === 'products') {
      csvContent = 'Product Name,Quantity Sold,Revenue,Avg Price\n';
      reportData.topProducts.forEach(prod => {
        const avgPrice = (prod.revenue / prod.quantity).toFixed(2);
        csvContent += `${prod.name},${prod.quantity},${prod.revenue.toFixed(2)},${avgPrice}\n`;
      });
    } else if (reportType === 'inventory') {
      csvContent = 'Barcode,Product Name,Type,Stock Quantity,Price,Status\n';
      products.forEach(prod => {
        const price = prod.type === 'fixed' ? prod.price : prod.pricePerKg;
        const status = prod.stockQty === 0 ? 'Out' : prod.stockQty < 10 ? 'Low' : 'Good';
        csvContent += `${prod.barcode},${prod.name},${prod.type},${prod.stockQty},${price.toFixed(2)},${status}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="reports-page">
      <AdminHeader title="Reports & Analytics" />

      <div className="reports-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading reports...</p>
          </div>
        ) : (
          <>
            <ReportFilters
              dateRange={dateRange}
              setDateRange={setDateRange}
              reportType={reportType}
              setReportType={setReportType}
              onExport={handleExport}
              onPrint={handlePrint}
            />

            <ReportSummary reportData={reportData} />

            {reportType === 'sales' && (
              <>
                <SalesChart invoices={filteredInvoices} />
                <ReportTable
                  data={filteredInvoices}
                  type="sales"
                  title="Sales Transactions"
                />
              </>
            )}

            {reportType === 'products' && (
              <ReportTable
                data={reportData.topProducts}
                type="products"
                title="Top Selling Products"
              />
            )}

            {reportType === 'inventory' && (
              <ReportTable
                data={products}
                type="inventory"
                title="Current Inventory Status"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;